import React from "react";
import { useStatsTooltip } from "./StatsTooltip";
import { BAR_TEXT_PX, barPercent } from "./statsBars";

// The bar takes at most this share of its track, leaving room for the value written at its tip.
const BAR_SHARE_OF_TRACK = 0.8;

const TEXT_STYLE = { fontSize: `${BAR_TEXT_PX}px` };

/**
 * Horizontal bar chart: one row per item (label, a single lime bar, the value at its tip), shared by
 * "Por tipo de turno" and "Por servicio".
 *
 * It is plain HTML laid out by CSS, not an SVG with a viewBox: the bars stretch with the container
 * but the text stays at a fixed size (`BAR_TEXT_PX`) on any screen width. A row whose value is zero
 * shows the value without a bar. The whole chart is one image for assistive tech named by
 * `ariaLabel`; the table twin ("Ver tabla") carries the details.
 *
 * @param {{rows: Array<{key: string, label: string, value: number, valueText: string}>,
 *   ariaLabel: string,
 *   tip?: (row: object) => {title: string, rows: Array<{value: string, label?: string}>}}} props
 *   `rows` come sorted; `tip` builds the hover tooltip of a row.
 */
export default function HorizontalBars({ rows, ariaLabel, tip }) {
  const tooltip = useStatsTooltip();
  const max = Math.max(...rows.map((row) => row.value), 0);

  return (
    <div className="stats-bars" role="img" aria-label={ariaLabel}>
      {rows.map((row) => {
        const percent = barPercent(row.value, max);
        return (
          <div
            key={row.key}
            className="stats-bar-row"
            data-bar={row.key}
            onPointerMove={
              tip
                ? (event) => {
                    const { title, rows: tipRows } = tip(row);
                    tooltip.show(title, tipRows, event.clientX, event.clientY);
                  }
                : undefined
            }
            onPointerLeave={tip ? tooltip.hide : undefined}
          >
            <span className="stats-bar-label" style={TEXT_STYLE}>
              {row.label}
            </span>
            <span className="stats-bar-track">
              {percent > 0 && <span className="stats-bar-fill" style={{ width: `${percent * BAR_SHARE_OF_TRACK}%` }} />}
              <span className="stats-bar-value" style={TEXT_STYLE}>
                {row.valueText}
              </span>
            </span>
          </div>
        );
      })}
    </div>
  );
}
